// src/pages/TripListPage.tsx
import React, { useState, useEffect } from 'react';
import { View, Heading, Collection, Card, Button, Text, Flex, Loader, Image, CheckboxField, Alert, Badge } from '@aws-amplify/ui-react';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { listTrips } from '../graphql/queries';
import { deleteTrip } from '../graphql/mutations';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './TripListPage.css';
import { listUserTripsWithPhotos } from '../graphql/customQueries';
import { getPhotoAccess } from '../graphql/queries';
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { Helmet } from 'react-helmet-async';
import { deleteTripAndAssociatedPhotos } from '../services/tripService';
import MemoryCarousel from '../components/MemoryCarousel';


const filmStripStyles = `
  .film-strip-container {
    position: relative;
    padding: 2rem 0;
    margin-bottom: 2rem;
  }
  
  .film-perforations {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    z-index: 10;
  }
  
  .film-perforations-left {
    left: 0;
  }
  
  .film-perforations-right {
    right: 0;
  }
  
  .film-perforation {
    width: 1rem;
    height: 1.5rem;
    background-color: #202020;
    border: 1px solid #333;
    border-radius: 2px;
  }

  /* Role Badge Styles */
  .role-badge {
    position: absolute;
    top: 10px;
    left: 10px;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 0.7rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    z-index: 2;
  }
  
  .role-badge.creator {
    background-color: rgba(49, 130, 206, 0.9);
    color: white;
  }
  
  .role-badge.traveler {
    background-color: rgba(72, 187, 120, 0.9);
    color: white;
  }
  
  .role-badge.viewer {
    background-color: rgba(160, 174, 192, 0.9);
    color: white;
  }
`;
// Create a client for API calls
const client = generateClient();

const TripListPage: React.FC = () => {
  const [trips, setTrips] = useState<any[]>([]);
  const [tripThumbnails, setTripThumbnails] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [selectedTrips, setSelectedTrips] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.sub) {
      fetchTrips();
    }
  }, [user?.sub]);

  // Load thumbnails after trips are fetched
  useEffect(() => {
    if (trips.length > 0) {
      loadTripThumbnails();
    }
  }, [trips]);

  // Reset selection mode when trips change
  useEffect(() => {
    setSelectionMode(false);
    setSelectedTrips(new Set());
  }, [trips]);

  async function fetchTrips() {
    try {
      setLoading(true);

      const tripsData: any = await client.graphql({
        query: listUserTripsWithPhotos,
        variables: {
          userId: user?.userId,
        },
        authMode: "userPool"
      });

      const tripsWithRoles = tripsData.data.tripMembersByUserId.items.map((membership: any) => {
        // Add the user's role to each trip object
        const trip = membership.trip;
        if (trip && !trip._deleted) {
          return {
            ...trip,
            userRole: membership.role || (trip.createdBy === user?.sub ? 'CREATOR' : 'UNKNOWN')
          };
        }
        return null;
      }).filter(Boolean);

      // Sort trips by creation date
      const sortedTrips = tripsWithRoles.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setTrips(sortedTrips);
    } catch (error) {
      console.error('Error fetching trips:', error);
    } finally {
      setLoading(false);
    }
  }

  // Load thumbnail images for each trip
  const loadTripThumbnails = async () => {
    try {
      const thumbnails: { [key: string]: string } = {};
      const session = await fetchAuthSession();

      for (const trip of trips) {
        if (trip.photos && trip.photos.items && trip.photos.items.length > 0) {
          const tripPhotos = trip.photos.items.filter((item: any) => !item._deleted);

          if (tripPhotos.length > 0) {
            const thumbnail = tripPhotos[Math.floor(Math.random() * tripPhotos.length)];

            try {
              const credentials = session.credentials;
              const response = await client.graphql({
                query: getPhotoAccess,
                variables: { photoId: thumbnail.id }, authMode: "userPool"
              });

              const photoAccess = response.data.getPhotoAccess;
              if (!photoAccess) {
                console.error(`No photo access information returned for photo ${thumbnail.id}`);
                return;
              }

              // Create an S3 client
              const s3Client = new S3Client({
                region: photoAccess.region,
                credentials: credentials
              });

              // Create a GetObjectCommand
              const command = new GetObjectCommand({
                Bucket: photoAccess.bucket,
                Key: photoAccess.key
              });

              const presignedUrl = await getSignedUrl(s3Client, command, {
                expiresIn: 3600
              });
              thumbnails[trip.id] = presignedUrl.toString();
            } catch (error) {
              console.error(`Error loading thumbnail for trip ${trip.id}:`, error);
            }
          }
        }
      }

      setTripThumbnails(thumbnails);
    } catch (err) {
      console.error('Error loading trip thumbnails:', err);
    }
  };

  // Toggle selection mode
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedTrips(new Set());
  };

  // Toggle trip selection
  const toggleTripSelection = (tripId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click from navigating

    setSelectedTrips(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(tripId)) {
        newSelected.delete(tripId);
      } else {
        newSelected.add(tripId);
      }
      return newSelected;
    });
  };

  // Get role badge for trip
  const getRoleBadge = (trip: any) => {
    // First check if it's the creator
    if (trip.createdBy === user?.sub) {
      return <div className="role-badge creator">Creator</div>;
    }

    // Otherwise, return badge based on role
    switch (trip.userRole) {
      case 'TRAVELER':
        return <div className="role-badge traveler">Traveler</div>;
      case 'VIEWER':
        return <div className="role-badge viewer">Viewer</div>;
      default:
        return null;
    }
  };

  // Delete selected trips
  // Update error handling in deleteSelectedTrips method
  const deleteSelectedTrips = async () => {
    if (selectedTrips.size === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedTrips.size} album${selectedTrips.size > 1 ? 's' : ''}? This will permanently remove all photos and cannot be undone.`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeleteSuccess(false);

    try {
      // Convert Set to Array before iterating
      const tripIdsToDelete = Array.from(selectedTrips);
      let unauthorizedErrors = 0;

      // Delete each selected trip
      for (const tripId of tripIdsToDelete) {
        try {
          await deleteTripAndAssociatedPhotos(tripId);
        } catch (error: unknown) {

          // Type guard to check if error is an Error object with a message
          if (error instanceof Error && error.message.includes("Not authorized")) {
            unauthorizedErrors++;
          } else {
            // Re-throw other errors
            throw error;
          }
        }
      }

      // Set appropriate error message if there were unauthorized attempts
      if (unauthorizedErrors > 0) {
        if (unauthorizedErrors === tripIdsToDelete.length) {
          setDeleteError("Only the album creator can delete the album.");
        } else {
          setDeleteError(`${unauthorizedErrors} album(s) could not be deleted. Only the trip creator can delete their albums.`);
        }

        // Still refresh the list and exit selection mode
        fetchTrips();
        setSelectedTrips(new Set());
        setSelectionMode(false);
        return;
      }

      // If we got here, all deletes were successful
      setDeleteSuccess(true);
      fetchTrips();
      setSelectedTrips(new Set());
      setSelectionMode(false);
    } catch (error: unknown) {
      console.error('Error deleting trips:', error);

      // Type guard for error handling
      if (error instanceof Error) {
        // Check for authorization errors
        if (error.message.includes("Not authorized")) {
          setDeleteError("Only the album creator can delete the album.");
        } else {
          setDeleteError('Failed to delete one or more Albums. Please try again.');
        }
      } else {
        setDeleteError('An unexpected error occurred while deleting albums.');
      }
    } finally {
      setIsDeleting(false);

      // Clear success message after 3 seconds
      if (deleteSuccess) {
        setTimeout(() => setDeleteSuccess(false), 3000);
      }
    }
  };

  // Handle card click
  const handleCardClick = (tripId: string) => {
    if (selectionMode) {
      toggleTripSelection(tripId, { stopPropagation: () => { } } as React.MouseEvent);
    } else {
      navigate(`/trips/${tripId}`);
    }
  };

  if (loading) {
    return (
      <View padding="2rem" textAlign="center">
        <Loader size="large" />
        <Text marginTop="1rem">Loading albums...</Text>
      </View>
    );
  }

  return (
    <View padding="2rem">
      <Helmet>
        <title>Albums - Banjara App</title>
      </Helmet>
      {/* Add the Memory Carousel */}
      <MemoryCarousel />
      {/* Header with action buttons */}
      <Flex direction="row" justifyContent="space-between" alignItems="center" marginBottom="2rem">
        <Heading level={2} className="section-heading">All Albums</Heading>
        <Flex gap="1rem">
          {trips.length > 0 && (
            <Button
              variation={selectionMode ? "primary" : "destructive"}
              onClick={toggleSelectionMode}
            >
              {selectionMode ? "Cancel Selection" : "Select Albums"}
            </Button>
          )}

          {selectionMode && (
            <Button
              variation="destructive"
              onClick={deleteSelectedTrips}
              isLoading={isDeleting}
              isDisabled={selectedTrips.size === 0}
            >
              Delete Selected ({selectedTrips.size})
            </Button>
          )}

          {!selectionMode && (
            <Button variation="primary" onClick={() => navigate('/trips/new')}>
              Create New Album
            </Button>
          )}
        </Flex>
      </Flex>

      {/* Status messages */}
      {deleteError && (
        <Alert
          variation="error"
          isDismissible={true}
          onDismiss={() => setDeleteError(null)}
          marginBottom="1rem"
        >
          {deleteError}
        </Alert>
      )}

      {deleteSuccess && (
        <Alert
          variation="success"
          isDismissible={true}
          onDismiss={() => setDeleteSuccess(false)}
          marginBottom="1rem"
        >
          Successfully deleted {selectedTrips.size > 0 ? selectedTrips.size : ''} album(s).
        </Alert>
      )}

      {/* Trip list */}
      {trips.length > 0 && (
        <div className="film-strip-container">
          <style>{filmStripStyles}</style>

          {/* Letterbox effect */}
          <Collection
            items={trips}
            type="grid"
            templateColumns={{ base: '1fr', medium: '1fr 1fr', large: '1fr 1fr 1fr' }}
            gap="1.5rem"
          >
            {(trip, index) => (
              <Card
                key={index}
                padding="0"
                borderRadius="small"
                variation={selectedTrips.has(trip.id) ? "outlined" : "elevated"}
                className={`scene-card ${selectionMode ? 'selection-mode' : ''} ${selectedTrips.has(trip.id) ? 'selected' : ''}`}
                onClick={() => handleCardClick(trip.id)}
                backgroundColor="#1A1A1A"
                color="white"
                style={{ position: 'relative', overflow: 'hidden', height: '300px' }}
              >
                {/* Selection checkbox - keep positioned absolutely on top */}
                {selectionMode && (
                  <View className="selection-checkbox" style={{ zIndex: 3 }}>
                    <CheckboxField
                      name={`trip-select-${trip.id}`}
                      checked={selectedTrips.has(trip.id)}
                      onChange={(e) => toggleTripSelection(trip.id, e as unknown as React.MouseEvent)}
                      onClick={(e) => e.stopPropagation()}
                      label=""
                    />
                  </View>
                )}

                {/* Role Badge - new addition */}
                {!selectionMode && getRoleBadge(trip)}

                {/* Background image - fills the entire card */}
                <View style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                  {tripThumbnails[trip.id] ? (
                    <div className="relative w-full h-full">
                      <Image
                        src={tripThumbnails[trip.id]}
                        alt={trip.tripName}
                        width="100%"
                        height="100%"
                        objectFit="cover"
                      />
                    </div>
                  ) : (
                    <Flex height="100%" alignItems="center" justifyContent="center" backgroundColor="#141414">
                      <Text fontSize="3rem" color="#333333">🎬</Text>
                    </Flex>
                  )}
                </View>

                {/* Translucent overlay - covers the entire card with gradient */}
                <View
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 70%, rgba(0,0,0,0.9) 100%)',
                    zIndex: 1
                  }}
                />

                {/* Play button icon in center */}
                <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 2 }}>
                  <div className="w-12 h-12 rounded-full bg-black/30 flex items-center justify-center border border-white/30">
                    <div className="w-0 h-0 border-l-[10px] border-l-white border-t-[7px] border-t-transparent border-b-[7px] border-b-transparent ml-1"></div>
                  </div>
                </div>

                {/* Trip info section - positioned at bottom */}
                <Flex
                  direction="column"
                  padding="1.5rem"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 2
                  }}
                >
                  <Heading level={3} color="white">{trip.tripName}</Heading>
                  {trip.description && (<Text
                    color="#DDDDDD"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      marginBottom: '0.5rem'
                    }}
                  >
                    {trip.description || 'No description available'}
                  </Text>)}

                  <Flex direction="row" gap="0.5rem" wrap="wrap">
                    {trip.startDate && (
                      <Text fontSize="0.9rem" color="#BBBBBB">
                        {new Date(trip.startDate).toLocaleDateString()} -
                        {trip.endDate ? new Date(trip.endDate).toLocaleDateString() : 'Ongoing'}
                      </Text>
                    )}

                    {trip.photos && trip.photos.items && (
                      <Text fontSize="0.9rem" color="#BBBBBB">
                        {trip.photos.items.filter((p: any) => !p._deleted).length} shots
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </Card>
            )}
          </Collection>
        </div>
      )}
    </View>
  );
};

export default TripListPage;